import React, { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';
import './OutlookEmailModal.css';

interface OutlookEmailModalProps {
  onClose: () => void;
  connectedEmail?: string;
}

const OutlookEmailModal: React.FC<OutlookEmailModalProps> = ({ onClose, connectedEmail }) => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSend = async () => {
    // Basic validation
    if (!to.trim()) {
      setError('Please enter at least one recipient');
      return;
    }
    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!body.trim()) {
      setError('Please enter a message');
      return;
    }

    setError(null);
    setSending(true);

    try {
      // Split multiple email addresses by comma or semicolon
      const toEmails = to.split(/[,;]/).map(e => e.trim()).filter(e => e);
      const ccEmails = cc ? cc.split(/[,;]/).map(e => e.trim()).filter(e => e) : undefined;
      const bccEmails = bcc ? bcc.split(/[,;]/).map(e => e.trim()).filter(e => e) : undefined;

      await apiService.sendOutlookEmail({
        to: toEmails.length === 1 ? toEmails[0] : toEmails,
        subject,
        body,
        cc: ccEmails && ccEmails.length > 0 ? (ccEmails.length === 1 ? ccEmails[0] : ccEmails) : undefined,
        bcc: bccEmails && bccEmails.length > 0 ? (bccEmails.length === 1 ? bccEmails[0] : bccEmails) : undefined,
      });

      setSuccess(true);

      // Close modal after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to send email');
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Cmd/Ctrl + Enter to send
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      handleSend();
    }
  };

  if (success) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="outlook-email-modal success-modal" onClick={(e) => e.stopPropagation()}>
          <div className="success-content">
            <div className="success-icon">✓</div>
            <h2>Email Sent Successfully!</h2>
            <p>Your email has been sent via Outlook</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="outlook-email-modal" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className="modal-header">
          <div>
            <h2>Compose Email</h2>
            {connectedEmail && (
              <p className="connected-account">
                Sending from: <strong>{connectedEmail}</strong>
              </p>
            )}
          </div>
          <button className="close-button" onClick={onClose} disabled={sending}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>To *</label>
            <input
              type="text"
              placeholder="recipient@example.com (separate multiple with commas)"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={sending}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Subject *</label>
            <input
              type="text"
              placeholder="Email subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
            />
          </div>

          {showAdvanced && (
            <>
              <div className="form-group">
                <label>CC</label>
                <input
                  type="text"
                  placeholder="cc@example.com (separate multiple with commas)"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  disabled={sending}
                />
              </div>

              <div className="form-group">
                <label>BCC</label>
                <input
                  type="text"
                  placeholder="bcc@example.com (separate multiple with commas)"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  disabled={sending}
                />
              </div>
            </>
          )}

          {!showAdvanced && (
            <button
              className="show-advanced-button"
              onClick={() => setShowAdvanced(true)}
              type="button"
            >
              + Add CC/BCC
            </button>
          )}

          <div className="form-group">
            <label>Message *</label>
            <textarea
              placeholder="Write your message here..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending}
              rows={12}
            />
          </div>

          <div className="keyboard-hint">
            Tip: Press Cmd/Ctrl + Enter to send
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button className="send-button" onClick={handleSend} disabled={sending}>
            {sending ? (
              <>
                <Loader2 size={16} className="spinning" />
                Sending...
              </>
            ) : (
              <>
                <Send size={16} />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OutlookEmailModal;
